"use client";

//region Imports
import React, {useEffect, useRef, useState, useMemo, useCallback} from "react";
import {useForm} from "react-hook-form";
import _ from "lodash";

import {$core, HttpStatus} from "@isudio/core";
import {$store} from '@isudio/globals';
import {pages, policies} from "@isudio/config";
import {getCountries, getCountryByCode, useMergeRefs} from "@isudio/utils";
import type {IUser, IContactPayload, IContact} from "@isudio/data";
import type {FooterElement, HeaderElement, BaseDialogProps} from "../";
import {LoadingSpinner, Confirm, ToolbarTop, ToolbarBottom, InputValidator, TextAreaValidator, PhoneInput, RowGrid, useDialogController, useTippyRegistry} from "../";
//endregion imports

//region Types & Interfaces
interface ContactProps extends BaseDialogProps {
  dialogOptions?: any;
  to?: string;
  toName?: string;
  onSuccess?: () => void;
  onClose?: () => void;
}
//endregion Types & Interfaces

export function Contact({
                         dialogOptions,
                         to,
                         toName,
                         onSuccess,
                         onClose
                       }: ContactProps) {
  //region Variables
  const [loading, setLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");
  const [resetKey, setResetKey] = useState(0);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const organizationRef = useRef<HTMLInputElement | null>(null);
  const designationRef = useRef<HTMLInputElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const lastFocusedRef = useRef<any | null>(null);

  const user: IUser = _.cloneDeep($store.User?.user);
  const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
  const transformCountry = (c: any) => {
    return {
      label: c.name.english,
      value: c.code.phone,
      code: c.code.short2.toLowerCase(),
      imageUrl: `/assets/fonts/flag-icons/flags/png/${c.code.short2.toLowerCase()}.png`
    };
  };

  const country = transformCountry(!_.isNothing(_.get(user, 'country.code', null)) ? getCountryByCode(user.country.code) : _.get($store.Device.data, "location.geoCode.country", $store.Data.countries$.getValue().default));
  const countries = getCountries().map((c: any) => {
    return {
      label: c.name,
      value: c.dialCode,
      code: c.code,
      imageUrl: c.imageUrl
    };
  });

  const userName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  const defaults: IContact = useMemo(() => ({
    to: (to || ""),
    name: (userName || ""),
    email: user?.email || "",
    country: user?.country || undefined,
    phone: user?.mobile || "",
    organization: user?.organization || "",
    designation: user?.designation || "",
    subject: "",
    message: ""
  }), [user]);

  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    reset,
    getValues,
    setFocus,
    setError,
    watch,
    formState: {
      isDirty,
      isValid,
      errors,
    }
  } = useForm<IContact>({
    criteriaMode: "all",
    defaultValues: defaults
  });

  const isDM = (!_.isNothing(to) && !_.isNothing(toName));
  //endregion Variables

  //region Form Validations
  const validations: any = {
    name: {
      required: "Name is required.",
      minLength: {
        value: userPolicy.name.minLength,
        message: `Name should have at least ${userPolicy.name.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.name.maxLength,
        message: `Name can't exceed ${userPolicy.name.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z ]*$/,
        message: "Invalid characters entered.",
      }
    },
    email: {
      required: "Email is required.",
      minLength: {
        value: userPolicy.email.minLength,
        message: `Email should have at least ${userPolicy.email.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.email.maxLength,
        message: `Email can't exceed ${userPolicy.email.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Please enter a valid email address.",
      },
      valueExists: {
        message: "Email “{value}” has already signed up.\nPlease sign in if it is yours or use a different email."
      }
    },
    phone: {
      minLength: {
        value: userPolicy.mobile.minLength,
        message: `Phone number should have at least ${userPolicy.mobile.minLength} numbers.`,
      },
      maxLength: {
        value: userPolicy.mobile.maxLength,
        message: `Phone number can't exceed ${userPolicy.mobile.maxLength} numbers.`,
      },
      pattern: {
        value: /^[0-9+ ]*$/,
        message: "Invalid characters entered.",
      }
    },
    organization: {
      maxLength: {
        value: userPolicy.organization.maxLength,
        message: `Organization name can't exceed ${userPolicy.organization.maxLength} characters.`,
      }
    },
    designation: {
      maxLength: {
        value: userPolicy.designation.maxLength,
        message: `Designation can't exceed ${userPolicy.designation.maxLength} characters.`,
      }
    },
    subject: {
      required: "Subject is required.",
      minLength: {
        value: userPolicy.subject.minLength,
        message: `Subject should have at least ${userPolicy.subject.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.subject.maxLength,
        message: `Subject can't exceed ${userPolicy.subject.maxLength} characters.`,
      }
    },
    message: {
      required: "Message is required.",
      minLength: {
        value: userPolicy.message.minLength,
        message: `Message should have at least ${userPolicy.message.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.message.maxLength,
        message: `Message can't exceed ${userPolicy.message.maxLength} characters.`,
      }
    }
  }
  //endregion Form Validations

  //region Form Registrations
  const {
    ref: nameRegisterRef,
    onBlur: nameRegisterOnBlur,
    ...nameRegister
  } = register("name", validations.name);

  const {
    ref: emailRegisterRef,
    onBlur: emailRegisterOnBlur,
    ...emailRegister
  } = register("email", _.omit(validations.email, "valueExists"));

  const {
    ref: phoneRegisterRef,
    onBlur: phoneRegisterOnBlur,
    ...phoneRegister
  } = register("phone", validations.phone);

  const {
    ref: organizationRegisterRef,
    onBlur: organizationRegisterOnBlur,
    ...organizationRegister
  } = register("organization", validations.organization);

  const {
    ref: designationRegisterRef,
    onBlur: designationRegisterOnBlur,
    ...designationRegister
  } = register("designation", validations.designation);

  const {
    ref: subjectRegisterRef,
    onBlur: subjectRegisterOnBlur,
    ...subjectRegister
  } = register("subject", validations.subject);

  const {
    ref: messageRegisterRef,
    onBlur: messageRegisterOnBlur,
    ...messageRegister
  } = register("message", validations.message);
  //endregion Form Registrations

  //region Methods & Event Handlers
  const {
    openDialog,
    closeDialog,
    setOnBeforeClose,
    setOnClose
  } = useDialogController();

  const {
    suppress,
    unsuppress
  } = useTippyRegistry();

  const hasValue = (v?: string | null) => !!v?.trim();

  const setFirstFocus = () => {
    if (!user) return nameRef?.current?.focus();
    if (!user?.mobile) return phoneRef?.current?.focus();
    if (!user?.organization) return organizationRef?.current?.focus();
    if (!user?.designation) return designationRef?.current?.focus();
    return subjectRef?.current?.focus();
  }

  const onPhoneChange = async (value: any) => {
    const country = value.country;
    void setValue("country", country, {shouldDirty: true});
    const phone = value.number;
    void setValue("phone", phone, {
      shouldValidate: false,
      shouldDirty: true
    });
  };

  const onPhoneBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length < 1) return;
    const dialCode = e.target.value.replace(/^\+(\d+).*/, "$1");
    const phone = e.target.value.replace(/^\+\d+\s*/, ``).replace(/\s/g, ``)
    if (dialCode === defaults.country?.dialCode && phone === defaults.phone) return;
    await phoneRegisterOnBlur(e);
    await trigger("phone");
  };

  const onReset = () => {
    setResetKey((prev) => prev + 1);
    reset();
    const t = setTimeout(() => {
      setFirstFocus();
      clearTimeout(t);
    }, 10);
  };

  const _onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onSend();
  };

  const _onSend = async () => {
    if (!isValid) return;
    suppress();
    await handleSubmit(onSend)();
  };

  const onSend = async () => {
    setLoading(true);
    const values = _.omitBy(getValues(), (value) => _.isNil(value) || _.isNothing(value));
    const phone = values.phone ? `+${values.country?.dialCode} ${values?.phone ? values.phone.replace(/(\d{3})(?=\d{3})/g, '$1 ').trim() : ''}` : null;

    const payload: IContactPayload = _.omitBy({
      ...values,
      phone,
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IContactPayload;

    await $core.contact(payload).then(async () => {
      setLoading(false);
      await closeDialog(true);
      onSuccess?.();

    }).catch((e: any) => {
      setLoading(false);
      if (e?.code === HttpStatus.BAD_REQUEST && _.isArray(e?.data) && e?.data?.length > 0) {
        const fieldErrors = e?.data?.filter((f: any) => ["invalidData", "validationError"].indexOf(f?.error) < 0);
        fieldErrors.forEach((f: any) => {
          const fieldName = f?.attribute as keyof IContact;
          const message = _.get(validations, `${fieldName}.${f?.error === "required" ? "required" : `${f?.error}.message`}`, null)
            .replace("{value}", getValues(fieldName));

          if (fieldName && message) {
            setError(fieldName, {
              type: "validate",
              message
            });
          }
        });
        _.toastError(`Contact form contains invalid data.\nPlease correct highlighted fields and submit again.`);

      } else {
        _.toastError(_.get(e, 'message'));
      }
    });
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 5;
    const HIDE_THRESHOLD = 25;
    const TOP_HIDE_THRESHOLD = 40;

    setIsAtBottom(prev => {
      // ✅ SHOW when near bottom
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;

      // ✅ HIDE when near top (priority)
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;

      // ✅ HIDE when moving away from bottom
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;

      return prev;
    });
  };

  const _onBeforeClose = useCallback(() => {
    if (!isDirty) return true;

    return new Promise<boolean>((resolve) => {
      suppress();

      openDialog(Confirm, {
        ...pages.confirm.properties,
        text: "Discard changes?",

        onYes: async () => {
          unsuppress();
          await closeDialog(true);
          resolve(true);
        },

        onNo: async () => {
          unsuppress();
          if (lastFocusedRef?.current) {
            if (lastFocusedRef.current === "phone") {
              phoneRef.current?.focus();
            } else {
              setFocus(lastFocusedRef.current);
            }
          }
          resolve(false);
        }
      });
    });
  }, [isDirty]);

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  //endregion Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setFirstFocus();
  }, []);

  useEffect(() => {
    setOnBeforeClose(_onBeforeClose);
  }, [setOnBeforeClose, _onBeforeClose]);

  useEffect(() => {
    setOnClose(_onClose);
  }, [setOnClose, _onClose]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: pages.contact.title,
      style: "text-sm sm:text-md md:text-lg font-bold text-white tracking-tight md:tracking-wider"
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !isDirty,
      onClick: () => onReset()
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: () => closeDialog()
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "send",
      type: "gradientButton",
      action: "submit",
      label: "Send",
      style: `btn-submit btn-${isValid && isDirty ? 'blue transition duration-200 hover:text-generic-hover hover:scale-105' : 'gray'}`,
      disabled: !isValid,
      onClick: () => _onSend(),
      onEnter: () => _onSend()
    },
    {
      name: "cancel",
      type: "gradientButton",
      label: "Cancel",
      style: "btn-cancel btn-gray-reverse hover:text-light-red",
      onClick: () => closeDialog(),
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const loadingSpinner = (
    <LoadingSpinner
      className="absolute"
      borderRadius="rounded-sm md:rounded-md"
      label="Sending message..."
    />
  );

  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      ref={footerRef}
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const contactForm = (
    <form
      onSubmit={_onSend}
      className={`form-${className} grid px-4 gap-4 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed`}>

      <div
        className="relative w-full h-full flex flex-col justify-center gap-2">
        <div
          className="font-black text-md text-black dark:text-ghost-white tracking-normal">
          {isDM ? `Connect with ${toName}` : pages.contact.heading}
        </div>
        {!isDM && (
          <div
            className="max-w-[90%] text-[10px] italic text-custom-gray-600 dark:text-pale-white tracking-wider whitespace-pre-line">
            {pages.contact.text}
          </div>
        )}
        <div
          className="absolute -top-2 -right-2 w-fit h-fit">
          <img
            src={`${pages.contact.stamp[_.isDark() ? "dark" : "light"]}`}
            alt=""
            className="w-30 h-auto object-contain border-none"
          />
        </div>
      </div>

      <div
        className="flex flex-col justify-center items-center self-center0">
        <InputValidator
          ref={useMergeRefs(nameRef, nameRegisterRef)}
          placeholder="Name*"
          minLength={userPolicy.name.minLength}
          maxLength={userPolicy.name.maxLength}
          mask={/[a-zA-Z ]/}
          format="properCase"
          defaultValue={(hasValue(userName) ? userName : "")}
          tabIndex={(hasValue(userName) ? -1 : 0)}
          readOnly={hasValue(userName)}
          value={watch("name")}
          error={errors.name?.message}
          {...nameRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = nameRegister.name)}
          onBlur={async (e) => {
            await nameRegisterOnBlur(e);
            await trigger("name");
          }}
        />
      </div>

      <RowGrid>
        <InputValidator
          ref={useMergeRefs(emailRef, emailRegisterRef)}
          placeholder="Email*"
          minLength={userPolicy.email.minLength}
          maxLength={userPolicy.email.maxLength}
          mask={/[a-zA-Z0-9@._%+-]/}
          format="email"
          defaultValue={(hasValue(user?.email) ? user?.email : "")}
          tabIndex={(hasValue(user?.email) ? -1 : 0)}
          readOnly={(hasValue(user?.email))}
          value={watch("email")}
          error={errors.email?.message}
          {...emailRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = emailRegister.name)}
          onBlur={async (e) => {
            await emailRegisterOnBlur(e);
            await trigger("email");
          }}
        />

        <PhoneInput
          key={`p-${resetKey}`}
          parentRef={phoneRegisterRef}
          inputRef={phoneRef}
          placeholder="Phone"
          minLength={userPolicy.mobile.minLength}
          maxLength={userPolicy.mobile.maxLength}
          mask={/[0-9]/}
          format="phone"
          countries={countries}
          country={country}
          listClass="top-10"
          initialValue={`+${user?.country?.dialCode || country.value} ${user?.mobile ? user?.mobile?.replace(/(\d{3})(?=\d{3})/g, '$1 ').trim() : ''}`}
          tabIndex={(hasValue(user?.mobile) ? -1 : 0)}
          readOnly={(hasValue(user?.mobile))}
          error={errors.phone?.message}
          name={phoneRegister.name}
          showToast={_.toastError}
          onChange={onPhoneChange}
          onFocus={() => (lastFocusedRef.current = phoneRegister.name)}
          onBlur={onPhoneBlur}
        />
      </RowGrid>

      <RowGrid>
        <InputValidator
          ref={useMergeRefs(organizationRef, organizationRegisterRef)}
          placeholder="Organization"
          maxLength={userPolicy.organization.maxLength}
          mask={/[a-zA-Z0-9@. ]/}
          defaultValue={(hasValue(user?.organization) ? user?.organization : "")}
          tabIndex={(hasValue(user?.organization) ? -1 : 0)}
          readOnly={(hasValue(user?.organization))}
          value={watch("organization")}
          error={errors.organization?.message}
          {...organizationRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = organizationRegister.name)}
        />

        <InputValidator
          ref={useMergeRefs(designationRef, designationRegisterRef)}
          placeholder="Designation"
          maxLength={userPolicy.designation.maxLength}
          mask={/[a-zA-Z0-9@ .()-]/}
          defaultValue={(hasValue(user?.designation) ? user?.designation : "")}
          tabIndex={(hasValue(user?.designation) ? -1 : 0)}
          readOnly={(hasValue(user?.designation))}
          value={watch("designation")}
          error={errors.designation?.message}
          {...designationRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = designationRegister.name)}
        />
      </RowGrid>

      <div
        className="flex flex-col justify-center items-center self-center0">
        <InputValidator
          ref={useMergeRefs(subjectRef, subjectRegisterRef)}
          placeholder="Subject*"
          minLength={userPolicy.subject.minLength}
          maxLength={userPolicy.subject.maxLength}
          error={errors.subject?.message}
          {...subjectRegister}
          showToast={_.toastError}
          value={watch("subject")}
          onFocus={() => (lastFocusedRef.current = subjectRegister.name)}
        />
      </div>

      <div
        className="flex flex-col justify-center items-center self-center0">
        <TextAreaValidator
          ref={useMergeRefs(messageRef, messageRegisterRef)}
          placeholder="Message*"
          minLength={userPolicy.message.minLength}
          maxLength={userPolicy.message.maxLength}
          error={errors.message?.message}
          {...messageRegister}
          showToast={_.toastError}
          value={watch("message")}
          onFocus={() => (lastFocusedRef.current = messageRegister.name)}
        />
      </div>

      <div
        className="w-full h-full flex flex-row justify-between">
        <div className="flex flex-row items-center justify-start gap-4">
          <div
            className="w-fit h-fit">
            <img
              src={`${pages.contact.forbes[_.isDark() ? "dark" : "light"]}`}
              alt=""
              className="w-auto h-13 object-contain border-none"
            />
          </div>
          <div
            className="w-fit h-fit">
            <img
              src={`${pages.contact.transform[_.isDark() ? "dark" : "light"]}`}
              alt=""
              className="w-auto h-13 object-contain border-none"
            />
          </div>
        </div>
        <div
          className="w-fit h-fit">
          <img
            src={`${pages.contact.logo[_.isDark() ? "dark" : "light"]}`}
            alt=""
            className="w-auto h-13 object-contain border-none"
          />
        </div>
      </div>
    </form>
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className} envelope
          flex-1 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
          ${isOverflow ? "pt-4" : "py-4"}
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      {contactForm}
    </div>
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
          absolute bottom-0 left-0 right-0 z-50
          transition-transform duration-300 ease-in-out
          ${showFooter ? "translate-y-0" : "translate-y-full"}
        `}
    >
      {toolbarBottom}
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onKeyDown={_onKeyDown}
      onWheel={(e) => e.stopPropagation()}>
      {loading && (
        loadingSpinner
      )}

      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}
